import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ubicacionService, { Ubicacion } from '../../services/ubicacionService';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';

const defaultForm: Ubicacion = {
  nroUbicacion: 0,
  nombreUbicacion: '',
  nroSuc: 0,
  direccion: '',
  telefono: '',
  responsable: ''
};

const toOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const UbicacionForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroUbicacion } = useParams();
  const [formData, setFormData] = useState<Ubicacion>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  const loadUbicacion = useCallback(async () => {
    if (!nroUbicacion || nroUbicacion === 'nuevo') return;
    try {
      setLoading(true);
      const ubicacion = await ubicacionService.getUbicacion(Number(nroUbicacion));
      setFormData(ubicacion);
    } catch (error) {
      enqueueSnackbar('Error al cargar la ubicación', { variant: 'error' });
      navigate('/configuraciones/ubicaciones');
    } finally {
      setLoading(false);
    }
  }, [nroUbicacion, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroUbicacion && nroUbicacion !== 'nuevo') loadUbicacion();
  }, [nroUbicacion, loadUbicacion]);

  useEffect(() => {
    const loadSucursales = async () => {
      try {
        setLoadingSucursales(true);
        const result = await sucursalesVentaService.getSucursalesVenta({ pageNumber: 1, pageSize: 100 });
        setSucursales(result.items || []);
      } catch (error) {
        console.error('Error al cargar sucursales:', error);
        enqueueSnackbar('No se pudieron cargar las sucursales', { variant: 'warning' });
      } finally {
        setLoadingSucursales(false);
      }
    };

    loadSucursales();
  }, [enqueueSnackbar]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroUbicacion') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else if (name === 'credito' || name === 'nroCentroCosto' || name === 'enConsignacion' || name === 'sincronizar') {
      setFormData(prev => ({ ...prev, [name]: toOptionalNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreUbicacion.trim()) {
        enqueueSnackbar('El nombre de la ubicación es requerido', { variant: 'warning' });
        return;
      }

      if (!Number.isFinite(formData.nroUbicacion) || formData.nroUbicacion <= 0) {
        enqueueSnackbar('El N° de ubicación debe ser mayor a 0', { variant: 'warning' });
        return;
      }

      if (!Number.isFinite(formData.nroSuc) || formData.nroSuc <= 0) {
        enqueueSnackbar('Debe seleccionar una sucursal de venta', { variant: 'warning' });
        return;
      }

      const nombre = formData.nombreUbicacion.trim();
      if (nombre.length > 30) {
        enqueueSnackbar('El nombre no puede superar los 30 caracteres', { variant: 'warning' });
        return;
      }

      const direccion = (formData.direccion || '').trim();
      if (direccion.length > 80) {
        enqueueSnackbar('La dirección no puede superar los 80 caracteres', { variant: 'warning' });
        return;
      }

      const payload: Ubicacion = {
        ...formData,
        nombreUbicacion: nombre,
        direccion: direccion || undefined,
      };

      if (nroUbicacion && nroUbicacion !== 'nuevo') {
        await ubicacionService.updateUbicacion(Number(nroUbicacion), payload);
      } else {
        await ubicacionService.createUbicacion(payload);
      }

      enqueueSnackbar('Ubicación guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/ubicaciones');
    } catch (error: any) {
      console.error('Error al guardar ubicación:', error);
      const responseData = error?.response?.data;
      const backendMessage = responseData?.message;
      const backendErrors = responseData?.errors && typeof responseData.errors === 'object'
        ? Object.entries(responseData.errors)
            .flatMap(([, value]) => (Array.isArray(value) ? value : [String(value)]))
            .join(' | ')
        : '';
      const finalMessage = [backendMessage, backendErrors].filter(Boolean).join(' - ') || 'Error al guardar la ubicación';
      enqueueSnackbar(finalMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroUbicacion && nroUbicacion !== 'nuevo' ? 'Editar Ubicación' : 'Nueva Ubicación'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField
                  name="nroUbicacion"
                  label="N° Ubicación"
                  type="number"
                  value={formData.nroUbicacion}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={!!(nroUbicacion && nroUbicacion !== 'nuevo')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  name="nombreUbicacion"
                  label="Nombre"
                  value={formData.nombreUbicacion}
                  onChange={handleChange}
                  fullWidth
                  required
                  inputProps={{ maxLength: 30 }}
                  helperText={`${formData.nombreUbicacion.length}/30`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="direccion"
                  label="Dirección"
                  value={formData.direccion || ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 80 }}
                  helperText={`${(formData.direccion || '').length}/80`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="telefono"
                  label="Teléfono"
                  value={formData.telefono || ''}
                  onChange={handleChange}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="responsable"
                  label="Responsable"
                  value={formData.responsable || ''}
                  onChange={handleChange}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel>Sucursal de venta</InputLabel>
                  <Select
                    value={formData.nroSuc > 0 ? formData.nroSuc : ''}
                    label="Sucursal de venta"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        nroSuc: value === '' ? 0 : Number(value)
                      }));
                    }}
                    disabled={loadingSucursales}
                  >
                    <MenuItem value="">
                      <em>Seleccione una sucursal</em>
                    </MenuItem>
                    {sucursales.map((sucursal) => (
                      <MenuItem key={sucursal.nroSuc} value={sucursal.nroSuc}>
                        {sucursal.nroSuc} - {sucursal.nombreSucursal}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/ubicaciones')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UbicacionForm;
